import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["hasSubmenu", "parentUrl", "isActivePrimaryNavItem", "children", "label"],
  _excluded2 = ["hovered"],
  _excluded3 = ["focused"];
import styled from 'styled-components';
import { mediaQueries } from 'developer-docs-shared-components/utils/mediaQueries';
import { EERIE, GYPSUM, OBSIDIAN } from 'HubStyleTokens/colors';
import UIRouterNavLink from 'ui-addon-react-router-dom/UIRouterNavLink';
import { useLocation } from 'react-router-dom';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIconButton from 'UIComponents/button/UIIconButton';
import { proxiedSubdomainBasePath } from 'developer-docs-shared-components/utils/urls';
import useFocused from 'UIComponents/hooks/useFocused';
import useHovered from 'UIComponents/hooks/useHovered';
import { getLocalizedBadge, isUrlLocalized } from '../utils/localization';
import I18n from 'I18n';
import useLocale from 'developer-docs-shared-components/hooks/useLocale';
import { LinkWithEnglishSuperset } from 'developer-docs-shared-components/components/Link';
import { getEnglishBaseUrl } from '../utils/urls';
import { getSharedPillLinkStyles } from '../utils/css';
import { useSelector } from 'react-redux';
import { selectUserGates } from '../selectors/userState';
import { developerPlatformNavConfig, addToConfigIfUngated } from '../config/nav/guides/developerPlatform';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Applied to the active parent `PrimaryLink` (a `UIRouterNavLink`).
 */
export const ACTIVE_LINK_CLASSNAME = 'PrimaryNavLink-active';
const ResetUL = styled.ul.withConfig({
  displayName: "PrimaryNav__ResetUL",
  componentId: "sc-1i6esct-0"
})(["padding:0;margin:0;list-style:none;"]);
const PrimaryLink = styled(UIRouterNavLink).withConfig({
  displayName: "PrimaryNav__PrimaryLink",
  componentId: "sc-1i6esct-1"
})(["", " font-weight:500;&.", "{background:", ";font-weight:600;}"], getSharedPillLinkStyles(OBSIDIAN), ACTIVE_LINK_CLASSNAME, GYPSUM);
const DropdownUL = styled(ResetUL).withConfig({
  displayName: "PrimaryNav__DropdownUL",
  componentId: "sc-1i6esct-2"
})(["background-color:", ";position:absolute;left:50%;transform:translateX(-50%);top:100%;visibility:hidden;opacity:0;transition:visibility 200ms,opacity 200ms;min-width:184px;width:100%;box-shadow:0 8px 24px 0 rgba(45,62,80,0.12);padding:22px 8px;text-align:center;z-index:3;a{font-weight:500;font-size:14.5px;display:block;padding:8px 0;}&::before{content:'';background:", ";position:absolute;height:3px;top:-3px;left:0;display:block;width:100%;}[aria-expanded='true']:not(.", ") ~ &{visibility:visible;opacity:1;}"], GYPSUM, OBSIDIAN, ACTIVE_LINK_CLASSNAME);
const StyledLI = styled.li.withConfig({
  displayName: "PrimaryNav__StyledLI",
  componentId: "sc-1i6esct-3"
})(["display:inline-block;margin:0 0.55vw;position:relative;padding:24px 0 20px 0;a{color:", ";&:hover,&:focus{color:", ";}&.", "{margin-right:12px;}}", "{margin-left:23px;margin-right:23px;}", "{&:first-child{margin-left:0;}}"], OBSIDIAN, OBSIDIAN, ACTIVE_LINK_CLASSNAME, mediaQueries.aboveLaptop, mediaQueries.belowLaptop);

/**
 * @example
 * ```jsx
 * <li>
 *    <PrimaryLink>API & Reference</PrimaryLink>
 *    <ScreenReaderButton>Show menu for API & Reference</ScreenReaderButton>
 *    <DropdownUL>{secondaryNav}</DropdownUL>
 * <li>
 * ```
 */
const LI = _ref => {
  let {
      hasSubmenu,
      parentUrl,
      isActivePrimaryNavItem,
      children,
      label
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const showCaret = hasSubmenu && !isActivePrimaryNavItem;
  const _useHovered = useHovered({}),
    {
      hovered
    } = _useHovered,
    hoveredProps = _objectWithoutPropertiesLoose(_useHovered, _excluded2);
  const _useFocused = useFocused({}),
    {
      focused
    } = _useFocused,
    focusedProps = _objectWithoutPropertiesLoose(_useFocused, _excluded3);
  const isExpanded = hovered || focused;
  return /*#__PURE__*/_jsxs(StyledLI, Object.assign({}, rest, hoveredProps, focusedProps, {
    expanded: isExpanded,
    children: [/*#__PURE__*/_jsx(PrimaryLink, {
      activeClassName: ACTIVE_LINK_CLASSNAME,
      to: parentUrl,
      "aria-expanded": isExpanded,
      children: label
    }), showCaret && /*#__PURE__*/_jsx(UIIconButton, {
      use: "unstyled",
      "aria-hidden": true,
      tabIndex: -1,
      children: /*#__PURE__*/_jsx(UIIcon, {
        name: "down",
        size: 12,
        color: EERIE
      })
    }), children]
  }));
};
const renderSubmenuLI = ({
  label,
  url,
  overrideUrl,
  locale
}, badge) => {
  let linkNode;
  const isLocalized = isUrlLocalized(url, locale);
  const linkNodeChildren = /*#__PURE__*/_jsxs(_Fragment, {
    children: [label, badge && /*#__PURE__*/_jsx(View, {
      marginInlineStart: "200",
      as: "span",
      children: getLocalizedBadge(badge)
    })]
  });
  if (!isLocalized) {
    // External EN-tagged link
    linkNode = /*#__PURE__*/_jsx(LinkWithEnglishSuperset, {
      href: getEnglishBaseUrl(overrideUrl || url),
      children: linkNodeChildren
    });
  } else {
    // Router-aware link in the same `html[lang]` site
    linkNode = /*#__PURE__*/_jsx(UIRouterNavLink, {
      to: overrideUrl || url,
      activeClassName: ACTIVE_LINK_CLASSNAME,
      exact: false,
      children: linkNodeChildren
    });
  }
  return /*#__PURE__*/_jsx("li", {
    children: linkNode
  }, label);
};
const PrimaryNav = ({
  primaryNavConfig
}) => {
  const location = useLocation();
  const locale = useLocale();
  const userGates = useSelector(selectUserGates);
  return /*#__PURE__*/_jsx("nav", {
    "aria-label": I18n.text('developer-docs-ui.accessibility.primaryNavLabel'),
    children: /*#__PURE__*/_jsx(ResetUL, {
      children: primaryNavConfig.map(({
        baseDirectory,
        label,
        getSecondaryNavConfigs
      }) => {
        const secondaryNavConfigs = getSecondaryNavConfigs();

        // DDO-639 Hardcoded check for the correct gate for the hidden nav item
        if (addToConfigIfUngated(baseDirectory, userGates.gates)) {
          secondaryNavConfigs.push(developerPlatformNavConfig);
        }

        /**
         * @default Hover dropdown when inactive, blue submenu nav when active
         *
         * If only one secondary nav config exists, show its items in a
         * hover dropdown at desktop. Otherwise, when this section is active,
         * show a blue submenu navigation menu beneath the primary nav.
         */
        const showSubmenuNavWhenActive = secondaryNavConfigs.length > 1;
        const [, currBasePath] = location.pathname.replace(proxiedSubdomainBasePath, '').split('/');
        const isActivePrimaryNavItem = `/${currBasePath}` === baseDirectory;
        const listItems = /*#__PURE__*/_jsx(_Fragment, {
          children: secondaryNavConfigs.map(secondaryNavConfig => {
            const {
              badge,
              sectionLabel,
              commonPath,
              items
            } = secondaryNavConfig();
            if (showSubmenuNavWhenActive) {
              // Render all secondary nav configs as LIs in a dropdown
              return renderSubmenuLI({
                label: sectionLabel,
                url: commonPath,
                locale
              }, badge === 'BETA' ? 'BETA_SUFFIX' : badge);
            }

            // Render single secondary nav config's items as individual LIs in the dropdown (e.g. for Getting Started)
            return items.map(item => {
              if ('url' in item) {
                return renderSubmenuLI(Object.assign({}, item, {
                  locale
                }));
              }
              if (item.redirectUrl) {
                return renderSubmenuLI({
                  label: item.label,
                  url: item.redirectUrl,
                  locale
                }, badge);
              }
              return null;
            });
          })
        });
        return /*#__PURE__*/_jsx(LI, {
          parentUrl: baseDirectory,
          hasSubmenu: !!secondaryNavConfigs.length,
          isActivePrimaryNavItem: isActivePrimaryNavItem,
          label: label,
          children: !isActivePrimaryNavItem && /*#__PURE__*/_jsx(DropdownUL, {
            children: listItems
          })
        }, label);
      })
    })
  });
};
export default PrimaryNav;